/**
 * Instrument-plugin scaffolding — mirrors the effect plugin layer
 * added in PR #110 but for the voice-producing side of the signal
 * chain. Gives us a registry-backed way to list / pick / construct
 * instruments without hardcoding every kind into UI dropdowns.
 *
 * This PR only registers wrapper shells. Each `createVoice` returns
 * an inert handle today — actual trigger dispatch still runs
 * through the engine's existing hardcoded switch (`triggerVoice`,
 * `scheduleSample`, `scheduleGranular`). The registry exists so a
 * future picker UI can list instruments in one place and so the
 * plugin-shaped architecture is ready for the Slicer-to-Sampler
 * promotion in P8c.
 *
 * Mapping between persisted \`PadContent.kind\` and registry ids:
 *   - \`synth\`    → 'procedural'
 *   - \`sample\`   → 'sampler'
 *   - \`granular\` → 'granular'
 *   - \`midi\`     → 'midi'
 *
 * When the full host migration ships, \`resolvePad\` + \`triggerVoice\`
 * will consult the registry instead of the union; both paths use
 * the same id strings so persisted pads map cleanly without a
 * data migration.
 */

import { registerInstrument } from './registry'
import type {
  InstrumentPlugin,
  InstrumentPresetValue,
  InstrumentVoiceHandle,
} from './types'

let registered = false

export function registerBuiltinInstruments(): void {
  if (registered) return
  registered = true

  registerInstrument(proceduralInstrument)
  registerInstrument(samplerInstrument)
  registerInstrument(granularInstrument)
  registerInstrument(midiInstrument)
}

/** Real sample-playback voice for the `sampler` instrument.
 *  Takes a preset with `{ buffer, pitch, sliceStart?, sliceLen? }`
 *  (buffer is the decoded AudioBuffer for the sample). The voice
 *  constructs a BufferSourceNode wired through a per-voice gain,
 *  schedules attack, and auto-disposes when the source ends. Host
 *  callers wire the output into a bus; dispose frees the node.
 *
 *  Pitch is applied multiplicatively on top of preset.pitch +
 *  trigger-time pitchSemitones. Velocity scales the gain node
 *  before the source emits, so velocity-layered multisamples can
 *  work without further plumbing. */
function createSamplerVoice(
  ctx: AudioContext,
  preset: Record<string, InstrumentPresetValue>,
): InstrumentVoiceHandle {
  const buffer =
    preset.buffer instanceof AudioBuffer ? preset.buffer : undefined
  const gain = ctx.createGain()
  gain.gain.value = 0
  let source: AudioBufferSourceNode | null = null
  let disposed = false

  const presetPitch = typeof preset.pitch === 'number' ? preset.pitch : 0
  const sliceStart =
    typeof preset.sliceStart === 'number' && preset.sliceStart >= 0
      ? preset.sliceStart
      : 0
  const sliceLen =
    typeof preset.sliceLen === 'number' && preset.sliceLen > 0
      ? preset.sliceLen
      : undefined

  return {
    output: gain,
    trigger(when, velocity, pitchSemitones) {
      if (disposed || !buffer) return
      source = ctx.createBufferSource()
      source.buffer = buffer
      const totalPitch = presetPitch + pitchSemitones
      if (totalPitch !== 0) {
        source.playbackRate.value = 2 ** (totalPitch / 12)
      }
      source.connect(gain)
      const attackT = Math.max(when, ctx.currentTime)
      // Fast AD envelope — 1 ms attack to avoid clicks at start,
      // hold at full velocity, let the buffer end naturally. An
      // explicit release via `release()` is what the host calls
      // for note-off.
      gain.gain.cancelScheduledValues(attackT)
      gain.gain.setValueAtTime(0, attackT)
      gain.gain.linearRampToValueAtTime(
        Math.max(0, Math.min(1, velocity)),
        attackT + 0.001,
      )
      if (sliceLen !== undefined) {
        source.start(attackT, sliceStart, sliceLen)
      } else if (sliceStart > 0) {
        source.start(attackT, sliceStart)
      } else {
        source.start(attackT)
      }
      source.onended = () => {
        // Auto-dispose on natural end so a pad hit doesn't leak
        // nodes. Explicit dispose() from the host cancels the
        // source early via source.stop() — the listener still fires
        // after stop and this path still runs.
        if (!disposed) {
          try {
            source?.disconnect()
          } catch {
            /* already */
          }
          source = null
        }
      }
    },
    release(when) {
      if (disposed || !source) return
      const releaseT = Math.max(when, ctx.currentTime)
      // Short release to avoid clicks; the source's natural end
      // still applies — release() just wraps an amp fade so the
      // tail doesn't truncate abruptly.
      gain.gain.cancelScheduledValues(releaseT)
      gain.gain.setValueAtTime(gain.gain.value, releaseT)
      gain.gain.linearRampToValueAtTime(0, releaseT + 0.02)
      try {
        source.stop(releaseT + 0.025)
      } catch {
        /* already stopped */
      }
    },
    dispose() {
      if (disposed) return
      disposed = true
      if (source) {
        try {
          source.stop()
        } catch {
          /* already stopped */
        }
        try {
          source.disconnect()
        } catch {
          /* already */
        }
        source = null
      }
      try {
        gain.disconnect()
      } catch {
        /* already */
      }
    },
  }
}

/** Every OTHER registered instrument returns this placeholder voice
 *  for now. The audio is silent — the engine's hardcoded switch is
 *  still authoritative for actual dispatch. Placeholder keeps the
 *  registry + picker UI honest about what's available. */
function inertVoice(ctx: AudioContext): InstrumentVoiceHandle {
  const gain = ctx.createGain()
  gain.gain.value = 0
  return {
    output: gain,
    trigger: () => {
      /* dispatched via engine's hardcoded switch until migration */
    },
    dispose: () => {
      try {
        gain.disconnect()
      } catch {
        /* already disconnected */
      }
    },
  }
}

const proceduralInstrument: InstrumentPlugin = {
  id: 'procedural',
  label: 'Procedural Kit Voice',
  category: 'synth',
  defaultPreset: { voice: 'kick' },
  paramSchema: [
    {
      id: 'voice',
      label: 'Voice',
      kind: 'select',
      options: [
        [0, 'kick'],
        [1, 'snare'],
        [2, 'hatClosed'],
        [3, 'hatOpen'],
        [4, 'clap'],
        [5, 'tom'],
        [6, 'ride'],
        [7, 'cowbell'],
      ],
      default: 0,
    },
  ],
  createVoice: ctx => inertVoice(ctx),
}

const samplerInstrument: InstrumentPlugin = {
  id: 'sampler',
  label: 'Sampler',
  category: 'sampler',
  defaultPreset: {},
  paramSchema: [
    {
      id: 'pitch',
      label: 'Pitch (semitones)',
      kind: 'slider',
      min: -24,
      max: 24,
      step: 0.1,
      default: 0,
    },
    {
      id: 'sliceStart',
      label: 'Slice start (s)',
      kind: 'time',
      min: 0,
      max: 120,
      step: 0.001,
      default: 0,
    },
    {
      id: 'sliceLen',
      label: 'Slice len (s)',
      kind: 'time',
      min: 0,
      max: 120,
      step: 0.001,
      default: 0,
    },
  ],
  // Real voice factory — plays the preset's AudioBuffer from
  // sliceStart for sliceLen seconds with pitch transposition via
  // playbackRate. Multisample zones aren't wired yet (a single
  // buffer is dispatched per voice); host layer will route
  // different MIDI notes to different SamplerInstance presets once
  // the zone editor UI lands. Velocity scales the output gain so
  // velocity-layered multisamples will work without additional
  // plumbing here.
  createVoice: (ctx, preset = {}) => createSamplerVoice(ctx, preset),
}

const granularInstrument: InstrumentPlugin = {
  id: 'granular',
  label: 'Granular',
  category: 'hybrid',
  defaultPreset: {
    grainSizeMs: 100,
    overlap: 0.25,
    detuneCents: 0,
    playbackRate: 1,
  },
  paramSchema: [
    {
      id: 'grainSizeMs',
      label: 'Grain size',
      kind: 'time',
      min: 10,
      max: 1000,
      step: 1,
      default: 100,
    },
    {
      id: 'overlap',
      label: 'Overlap',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.25,
    },
    {
      id: 'detuneCents',
      label: 'Detune',
      kind: 'slider',
      min: -2400,
      max: 2400,
      step: 1,
      default: 0,
    },
    {
      id: 'playbackRate',
      label: 'Rate',
      kind: 'slider',
      min: 0.25,
      max: 4,
      step: 0.01,
      default: 1,
    },
  ],
  createVoice: ctx => inertVoice(ctx),
}

const midiInstrument: InstrumentPlugin = {
  id: 'midi',
  label: 'External MIDI',
  category: 'external',
  defaultPreset: { channel: 1 },
  paramSchema: [
    {
      id: 'channel',
      label: 'Channel',
      kind: 'slider',
      min: 1,
      max: 16,
      step: 1,
      default: 1,
    },
  ],
  createVoice: ctx => inertVoice(ctx),
}
